import executeGraphQlQuery from 'helpers/storefront-graphql-helper'
import { getMultipleGraphQLIds, getSingleGraphQLId } from 'libs/shopify'

/**
 * Normalize GraphQL product fields.
 *
 * @param {number} imageCount - Number of images to fetch.
 * @param {number} variantCount - Number of variants to fetch.
 * @param {string} additionalQuery - Additional query fields.
 * @return {string} - GraphQL query string.
 */
const normalizeGqlProductFields = (imageCount = 5, variantCount = 50, additionalQuery = '') => `
  availableForSale
  id
  vendor
  productType
  title
  handle
  onlineStoreUrl
  isGiftCard
  featuredImage {
    id
    src
    width
    height
    altText
  }
  images(first: ${imageCount}) {
    nodes {
      id
      src
      width
      height
      altText
    }
  }
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  compareAtPriceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  variants(first: ${variantCount}) {
    nodes {
      availableForSale
      id
      sku
      title
      quantityAvailable
      price {
        amount
        currencyCode
      }
      compareAtPrice {
        amount
        currencyCode
      }
      selectedOptions {
        name
        value
      }
      image {
        id
        src
        width
        height
        altText
      }
    }
  }
  tags
  metafields(identifiers: [
    { namespace: "linked", key: "size" },
    { namespace: "seo", key: "hidden" },
    { namespace: "custom", key: "scent" },
    { namespace: "custom", key: "award_badge" }
  ]) {
    key
    namespace
    value
    reference {
      ... on MediaImage {
        id
        image {
          url
          altText
          width
          height
        }
      }
      ... on Metaobject {
        id
        type
        fields {
          key
          value
        }
      }
    }
  }
  ${additionalQuery}
`

const normalizeGqlProductBundleFields = `
  id
  handle
  title
  variants(first: 50) {
    nodes {
      components(first: 100) {
        nodes {
          productVariant {
            id
            product {
              handle
              id
            }
          }
        }
      }
    }
  }
`

/**
 * Build a GraphQL query.
 *
 * @param {string} query - GraphQL query string.
 * @param {object} variables - GraphQL query variables.
 * @return {object} - GraphQL query object.
 */
const buildQuery = (query, variables) => ({ query, variables })

/**
 * Build a GraphQL query for fetching products by IDs.
 *
 * @param {Array<string>} ids - Array of product IDs.
 * @return {object} - GraphQL query object.
 */
const buildProductsQuery = (ids) => {
  const countryCode = GM_STATE.shopify.storeCountry

  return buildQuery(`
    query Products ($ids: [ID!]!) @inContext(country: ${countryCode})  {
      nodes(ids: $ids) {
        ... on Product {
          ${normalizeGqlProductFields()}
        }
      }
    }
  `, { ids })
}

/**
 * Build a GraphQL query for fetching products by collection handle.
 *
 * @param {string} handle - Collection handle.
 * @param {Array<object>} filters - Array of product filters.
 * @param {number} size - Number of products to fetch.
 * @return {object} - GraphQL query object.
 */
const buildCollectionsQuery = (handle, filters, size) => {
  const countryCode = GM_STATE.shopify.storeCountry

  return buildQuery(`
    query Collection($handle: String, $first: Int, $filters: [ProductFilter!]) @inContext(country: ${countryCode}) {
      collection(handle: $handle) {
        products(first: $first, filters: $filters) {
          nodes {
            ${normalizeGqlProductFields()}
          }
        }
      }
    }
  `, { handle, filters, first: size })
}

/**
 * Build a GraphQL query for fetching recommended products by ID.
 *
 * @param {string} id - Product ID.
 * @param {string} intent - Recommendation intent.
 * @return {object} - GraphQL query object.
 */
const buildRecommendationsQuery = (id, intent) => {
  const countryCode = GM_STATE.shopify.storeCountry

  return buildQuery(`
    query ProductRecommendations($id: ID!, $intent: ProductRecommendationIntent) @inContext(country: ${countryCode}) {
      productRecommendations(productId: $id, intent: $intent) {
        ${normalizeGqlProductFields()}
      }
    }
  `, { id, intent })
}

const buildQueryNewestArticles = (limit = 1) => {
  const variables = { limit }
  const query = `
    query Articles($limit: Int) {
      articles(first: $limit, reverse: true, sortKey: UPDATED_AT) {
        nodes {
          title
          onlineStoreUrl
          image {
            url
          }
          tags
          publishedAt
          blog {
            title
          }
        }
      }
    }
  `

  return {
    query,
    variables
  }
}

const buildSearchProductBundlesQuery = (limit, tag, query) => {
  const countryCode = GM_STATE.shopify.storeCountry

  return buildQuery(`
    query searchProductBundles($tag: String,  $first: Int, $query: String!) @inContext(country: ${countryCode}) {
      search(productFilters: {tag: $tag}, first: $first, query: $query) {
        nodes {
          ... on Product {
            ${normalizeGqlProductBundleFields}
          }
        }
      }
    }
  `, { first: limit, tag, query })
}

/**
 * Fetch data from GraphQL API.
 *
 * @param {object} gqlQuery - GraphQL query object.
 * @return {Promise<object>} - GraphQL response data.
 */
const fetchGraphQLData = async (gqlQuery) => {
  const response = await executeGraphQlQuery(gqlQuery)

  if (!response.ok) throw new Error(`GraphQL query failed: ${response.statusText}`)

  const body = await response.json()

  return body.data
}

/**
 * Fetch products by collection handle.
 *
 * @param {string} handle - Collection handle.
 * @param {Array<object>} filters - Array of product filters.
 * @param {number} size - Number of products to fetch.
 * @return {Promise<Array<object>>} - Array of products.
 */
const fetchProductByCollectionHandle = async (handle, filters = [], size = 10) => {
  const gqlQuery = buildCollectionsQuery(handle, filters, size)
  const data = await fetchGraphQLData(gqlQuery)

  return data.collection.products.nodes
}

/**
 * Fetch products by IDs.
 *
 * @param {Array<string>} ids - Array of product IDs.
 * @return {Promise<Array<object>>} - Array of products.
 */
const fetchProductByIds = async (ids) => {
  const gids = getMultipleGraphQLIds(ids, 'Product')
  const gqlQuery = buildProductsQuery(gids)
  const data = await fetchGraphQLData(gqlQuery)

  return data.nodes
}

/**
 * Fetch recommended products by ID.
 *
 * @param {string} id - Product ID.
 * @param {string} intent - Recommendation intent.
 * @return {Promise<Array<object>>} - Array of recommended products.
 */
const fetchRecommendedProductById = async (id, intent = 'RELATED') => {
  const gid = getSingleGraphQLId(id, 'Product')
  const gqlQuery = buildRecommendationsQuery(gid, intent)
  const data = await fetchGraphQLData(gqlQuery)

  return data.productRecommendations
}

const fetchProductBundles = async (limit = 100, tag = 'bundles', query = '') => {
  const gqlQuery = buildSearchProductBundlesQuery(limit, tag, query)
  const data = await fetchGraphQLData(gqlQuery)

  return data.search.nodes
}

const fetchNewestArticles = async (limit) => {
  const gqlQuery = buildQueryNewestArticles(limit)
  const articles = executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) throw res

      return res.json()
    }).then((body) => body.data.articles.nodes)

  return articles
}

export {
  fetchProductByCollectionHandle,
  fetchRecommendedProductById,
  fetchProductByIds,
  normalizeGqlProductFields,
  fetchNewestArticles,
  fetchProductBundles
}
